import { monotonicDeadline, secondsUntilDeadline } from './date';

/**
 * A countdown is either running against a deadline on the monotonic clock — which a
 * change to the device clock cannot cut short or stretch — or holding a fixed number of
 * seconds, while whoever is being timed takes a break or once they are done. Keeping how
 * it is keeping time in the same value as whether it is keeping time at all is what stops
 * a held countdown from quietly running down against a deadline nobody is watching.
 *
 * A running countdown may also be carrying `syncedDeadline` — where a sync last said the
 * deadline really is — while its owner works the difference off. Living inside the value
 * means pausing, stopping or restarting drops it by construction; a stale correction
 * cannot outlive the deadline it was measured against.
 */
export type Countdown =
  | { status: 'running'; deadline: number; syncedDeadline?: number }
  | { status: 'paused'; remainingInSeconds: number }
  | { status: 'stopped'; remainingInSeconds: number };

/** A countdown running from now until `seconds` from now. */
export function running(seconds: number): Countdown {
  return { status: 'running', deadline: monotonicDeadline(seconds) };
}

/** The seconds a countdown has left, whichever way it is currently keeping time. */
export function remainingSeconds(countdown: Countdown) {
  return countdown.status === 'running'
    ? secondsUntilDeadline(countdown.deadline)
    : countdown.remainingInSeconds;
}

/**
 * The whole seconds a countdown displays, or nothing at all when it has no time to
 * report — showing no countdown beats showing one made of NaN. Time already run out reads
 * as zero rather than as a negative.
 */
export function secondsLeft(countdown: Countdown) {
  const remaining = remainingSeconds(countdown);

  if (!Number.isFinite(remaining)) {
    return undefined;
  }

  return Math.floor(Math.max(remaining, 0));
}
